import { getPurchaseContext, storePurchaseContext } from "@/libs/ga";
import {
  hideRedirectLoading,
  redirectTo,
  REDIRECT_MESSAGES,
  showRedirectLoading,
} from "@/libs/redirectLoading";
import { startStripeCheckout, type StripeCheckoutParams } from "@/libs/stripeCheckout";

export interface PendingCheckoutIntent {
  priceId: string;
  mode: "payment" | "subscription";
  quantity?: number;
  couponId?: string;
  credits?: number;
  purchaseTrigger?: "voluntary" | "forced";
  resumeAfterAuth?: boolean;
}

/** Survives for the Google popup flow (no page reload). */
let inMemoryIntent: PendingCheckoutIntent | null = null;

const CHECKOUT_PRICE_PARAM = "pending_checkout";
const CHECKOUT_MODE_PARAM = "checkout_mode";

export const PRICING_FALLBACK_URL = "/pricing";

function redirectToPricing(): void {
  if (typeof window === "undefined") return;
  redirectTo(PRICING_FALLBACK_URL, REDIRECT_MESSAGES.redirecting);
}

function intentFromSessionStorage(): PendingCheckoutIntent | null {
  const ctx = getPurchaseContext(false);
  if (!ctx?.price_id) return null;
  if (ctx.resume_after_auth === false) return null;

  return {
    priceId: ctx.price_id as string,
    mode: (ctx.mode as "payment" | "subscription") || "subscription",
    quantity: typeof ctx.quantity === "number" ? ctx.quantity : undefined,
    couponId: typeof ctx.coupon_id === "string" ? ctx.coupon_id : undefined,
    credits: typeof ctx.credits === "number" ? ctx.credits : undefined,
    purchaseTrigger:
      ctx.purchase_trigger === "forced" ? "forced" : "voluntary",
    resumeAfterAuth: ctx.resume_after_auth !== false,
  };
}

function intentFromUrl(): PendingCheckoutIntent | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const priceId = params.get(CHECKOUT_PRICE_PARAM);
  const mode = params.get(CHECKOUT_MODE_PARAM);
  if (!priceId) return null;

  return {
    priceId,
    mode: mode === "payment" ? "payment" : "subscription",
    resumeAfterAuth: true,
  };
}

function persistToSessionStorage(intent: PendingCheckoutIntent): void {
  try {
    storePurchaseContext({
      price_id: intent.priceId,
      mode: intent.mode,
      credits: intent.credits,
      quantity: intent.mode === "subscription" ? 1 : intent.quantity,
      coupon_id: intent.couponId,
      purchase_trigger: intent.purchaseTrigger ?? "voluntary",
      package_type: intent.mode === "subscription" ? "subscription" : "topup",
      resume_after_auth: intent.resumeAfterAuth !== false,
    });
  } catch (error) {
    console.warn("[pendingCheckout] sessionStorage unavailable:", error);
  }
}

/** Remember the plan the user picked before sign-in. */
export function setPendingCheckout(intent: PendingCheckoutIntent): void {
  inMemoryIntent = intent;
  persistToSessionStorage(intent);
}

/** Read pending checkout from memory, sessionStorage, or URL params. */
export function getPendingCheckout(): PendingCheckoutIntent | null {
  if (inMemoryIntent?.priceId) return inMemoryIntent;

  const fromStorage = intentFromSessionStorage();
  if (fromStorage) {
    inMemoryIntent = fromStorage;
    return fromStorage;
  }

  const fromUrl = intentFromUrl();
  if (fromUrl) {
    inMemoryIntent = fromUrl;
    return fromUrl;
  }

  return null;
}

export function clearPendingCheckout(): void {
  inMemoryIntent = null;
  try {
    const ctx = getPurchaseContext(false);
    if (ctx?.price_id && ctx?.mode) {
      storePurchaseContext({
        price_id: ctx.price_id,
        mode: ctx.mode,
        ...ctx,
        resume_after_auth: false,
      });
    }
  } catch {
    // Non-critical.
  }
}

/** Build callback URL with checkout intent encoded for email magic-link return. */
export function buildAuthCallbackUrl(
  pathname: string,
  search: string,
  intent: PendingCheckoutIntent
): string {
  const params = new URLSearchParams(search);
  params.set(CHECKOUT_PRICE_PARAM, intent.priceId);
  params.set(CHECKOUT_MODE_PARAM, intent.mode);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function toStripeCheckoutParams(
  intent: PendingCheckoutIntent,
  options?: { waitForSession?: boolean }
): StripeCheckoutParams {
  return {
    priceId: intent.priceId,
    mode: intent.mode,
    quantity: intent.quantity,
    couponId: intent.couponId,
    credits: intent.credits,
    purchaseTrigger: intent.purchaseTrigger,
    waitForSession: options?.waitForSession,
  };
}

/** Resume Stripe checkout from whichever fallback still has the intent. */
export async function resumePendingCheckout(options?: {
  waitForSession?: boolean;
  /** Redirect to /pricing when checkout cannot be resumed. Default true. */
  redirectOnFailure?: boolean;
  /** Use when storage fallbacks are gone but purchase intent is still known. */
  intent?: PendingCheckoutIntent;
  /** When true, skip the sitewide redirect overlay (button-level loading instead). */
  skipRedirectOverlay?: boolean;
}): Promise<boolean> {
  const {
    waitForSession = false,
    redirectOnFailure = true,
    intent: explicitIntent,
    skipRedirectOverlay = false,
  } = options ?? {};
  const intent = explicitIntent ?? getPendingCheckout();
  if (!intent?.priceId) return false;

  if (!skipRedirectOverlay) {
    showRedirectLoading(REDIRECT_MESSAGES.checkout);
  }

  const ok = await startStripeCheckout({
    ...toStripeCheckoutParams(intent, { waitForSession }),
    skipRedirectOverlay,
  });

  if (ok) {
    clearPendingCheckout();
    return true;
  }

  if (!skipRedirectOverlay) {
    hideRedirectLoading();
  }
  if (redirectOnFailure) {
    redirectToPricing();
  }
  return false;
}
